import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ASSIGNMENT_TYPES, TASK_STATUSES, TAssignmentTypeValues } from '@/types/assignments';
import { Status, STATUSES } from '@/types/store';
import { RootState } from '..';
import { createAppAsyncThunk } from '../withTypes';
import { GenericAssignment, Note } from '@/types/assignmentsGeneral';
import { getMockPronounsAssignment } from '@/mock-data/pronounsAssignments';
import { errorMessages } from '@/utils/errorMessages';
import { showToast } from '@/utils/utils';

// TODO: After MVP, check if it's possible place slice inside projects,
// so projectId is not needed here as it's already inside the parent, projects slice.

interface AssignmentsState {
  projects: Record<number, Record<number, GenericAssignment>>;
  status: Status;
  error: string;
}

const initialState: AssignmentsState = {
  projects: {},
  status: STATUSES.PENDING,
  error: '',
};

interface UpdateAssignmentPayloadPartial {
  projectId: number;
  assignmentId: number;
}

interface UpdateTaskPayloadPartial extends UpdateAssignmentPayloadPartial {
  taskId: number;
}

interface BookmarkTaskPayload extends UpdateTaskPayloadPartial {
  isBookmarked: boolean;
}

interface CreateNotePayload extends UpdateTaskPayloadPartial {
  note: {
    text: Note['text'];
    userDetails: Note['userDetails'];
  };
}

interface UpdateNotePayload extends UpdateTaskPayloadPartial {
  noteId: Note['id'];
  text: Note['text'];
}

interface UpdatePronounsTaskPayload extends UpdateTaskPayloadPartial {
  translations: string[];
}

const fetchAssignmentPerType = {
  [ASSIGNMENT_TYPES.PRONOUNS]: getMockPronounsAssignment,
};

/* THUNKS */
export const fetchAssignment = createAppAsyncThunk('assignments/fetchAssignment', async ({ assignmentId, assignmentType }: { projectId: number, assignmentId: number, assignmentType: TAssignmentTypeValues }) => {
  const mockAssignment = fetchAssignmentPerType[assignmentType](assignmentId);
  const response: GenericAssignment = await new Promise((resolve) => {
    setTimeout(() => {
      resolve(mockAssignment);
    }, 2000);
  });

  return response;
});
/* END THUNKS */

const getAssignmentProjectState = (state: AssignmentsState, projectId: number) => {
  if (!state.projects[projectId]) {
    state.projects[projectId] = {};
  }

  return state.projects[projectId];
};

const assignmentsGeneralSlice = createSlice({
  name: 'assignmentsGeneral',
  initialState: initialState,
  reducers: {
    /**
     * Generic reducers for all types of assignments or tasks.
     **/
    bookmarkTask: (state, action: PayloadAction<BookmarkTaskPayload>) => {
      const { projectId, assignmentId, taskId, isBookmarked } = action.payload;

      const assignment = state.projects[projectId]?.[assignmentId];

      if (!assignment) {
        return;
      }

      const { tasks } = assignment;

      const task = tasks.find(({ id }) => id === taskId);

      if (task) { // TODO: Create getAssignment and getTask functions to generalize repeated code.
        task.isBookmarked = isBookmarked;
      }
    },
    completeTask: (state, action: PayloadAction<UpdateTaskPayloadPartial>) => {
      const { projectId, assignmentId, taskId } = action.payload;

      const assignment = state.projects[projectId]?.[assignmentId];

      if (!assignment) {
        return;
      }

      const task = assignment.tasks.find(({ id }) => id === taskId);

      if (task) {
        task.status = TASK_STATUSES.COMPLETED;
      }
    },
    createNote: (state, action: PayloadAction<CreateNotePayload>) => {
      const { projectId, assignmentId, taskId, note } = action.payload;
      const assignment = state.projects[projectId]?.[assignmentId];

      if (!assignment) {
        return;
      }

      const task = assignment.tasks.find(({ id }) => id === taskId);

      if (!task || !task.notes) {
        return;
      }

      const lastId = (task.notes || []).reduce((lastId, note) => Math.max(note.id || 0, lastId), 0);

      const _note: Note = {
        ...note,
        id: lastId + 1, // TODO: Backend should be in charge of generating id AND assigning createdAt/updatedAt
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      task.notes.push(_note);
    },
    updateNote: (state, action: PayloadAction<UpdateNotePayload>) => {
      const { projectId, assignmentId, taskId, noteId, text } = action.payload;
      const assignment = state.projects[projectId]?.[assignmentId];

      if (!assignment) {
        return;
      }

      const task = assignment.tasks.find(({ id }) => id === taskId);

      if (!task || !task.notes) {
        return;
      }

      const noteIndex = task.notes?.findIndex(({ id }) => noteId === id);

      if (noteIndex !== -1) {
        task.notes[noteIndex] = {
          ...task.notes[noteIndex],
          text,
        };
      }
    },
    /**
     * Reducers for specific types of assignments or tasks.
     **/
    updatePronounsTask: (state, action: PayloadAction<UpdatePronounsTaskPayload>) => {
      const { projectId, assignmentId, taskId } = action.payload;

      const assignment = state.projects[projectId]?.[assignmentId];

      if (!assignment) {
        return;
      }

      const pronounsTask = assignment.tasks.find(({ id }) => id === taskId);

      if (!pronounsTask) {
        return;
      }
      pronounsTask.status = TASK_STATUSES.COMPLETED;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAssignment.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchAssignment.fulfilled, (state, action) => {
      const { projectId, assignmentId } = action.meta.arg;
      const project = getAssignmentProjectState(state, projectId);

      project[assignmentId] = action.payload;

      state.status = STATUSES.FULFILLED;
    });
    builder.addCase(fetchAssignment.rejected, () => {
      showToast(errorMessages.fetchingAssignment, 'error');
    });
  },
});

export const selectProjectAssignments = (state: RootState, projectId: number) => state.assignmentsGeneral.projects[projectId];
export const selectAssignmentById = (state: RootState, projectId: number, assignmentId: number) => state.assignmentsGeneral.projects[projectId]?.[assignmentId] || null;
export const selectAssignmentTasks = createSelector([selectAssignmentById], assignment => assignment?.tasks || []);
export const selectAssignmentTotalTasks = createSelector([selectAssignmentTasks], tasks => tasks.length);
export const selectAssignmentCompletedTasks = createSelector([selectAssignmentTasks], (tasks) => {
  return tasks.filter(({ status }) => status === TASK_STATUSES.COMPLETED).length;
});

export const selectTaskNotes = createSelector([selectAssignmentById, (_: unknown, __: unknown, ___: unknown, selectedTaskId: number | null) => selectedTaskId], (assignment, selectedTaskId) => {
  if (!assignment || !selectedTaskId) {
    return [];
  }

  const task = assignment?.tasks.find(({ id }) => id === selectedTaskId);

  return task?.notes || [];
});

export const selectAssignmentsStatus = (state: RootState) => state.assignmentsGeneral?.status;
export const selectAssignmentsError = (state: RootState) => state.assignmentsGeneral?.error;

export const { bookmarkTask, completeTask, createNote, updateNote, updatePronounsTask } = assignmentsGeneralSlice.actions;

export default assignmentsGeneralSlice.reducer;
